import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface Account { id: string; name: string; region: string; is_active?: number }

interface PerfMetrics {
  spend: number; sales: number; orders: number;
  impressions: number; clicks: number; acos: number | null;
}
interface OverviewData {
  thisWeek: PerfMetrics; lastWeek: PerfMetrics;
  activeCampaigns: number; pendingActions: number;
  marketplaces: string[];
  topCampaigns: Array<{ id: string; name: string; marketplace?: string; spend: number; sales: number; acos: number | null; roas: number | null; budgetUtilization: number | null }>;
}

const MP_NAMES: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
};

const DATE_RANGES = [
  { days: 1, label: 'Yesterday' },
  { days: 7, label: '7d' },
  { days: 14, label: '14d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];
interface AutoStatus {
  rules: Array<{ algorithm_type: string; is_active: number; cnt: number }>;
  recentActivity: Array<{ algorithm_type: string; mode: string; change_type: string; cnt: number }>;
  criteriaProfiles: number;
  objectives: number;
  activeAssignments: number;
}
interface LogEntry {
  algorithm_type: string; rule_name: string; campaign_name: string;
  keyword_text: string | null; search_term: string | null;
  change_type: string; old_value: string; new_value: string;
  mode: string; created_at: string;
}
interface OutcomeData {
  summary: { total: number; improved: number; worsened: number; neutral: number; successRate: number };
}

// ── Constants ────────────────────────────────────────────────────────────────

const ALGO_META: Record<string, { label: string; color: string; icon: string; desc: string }> = {
  bidding:       { label: 'Bidding',      color: 'blue',   icon: '$$',  desc: 'Adjusts keyword bids to hit target ACoS using RPC formula' },
  negative:      { label: 'Negative',     color: 'red',    icon: '−',   desc: 'Negates poor-performing search terms with blacklist/whitelist' },
  import:        { label: 'Import',       color: 'green',  icon: '+',   desc: 'Promotes winning search terms to exact/broad keywords' },
  status:        { label: 'Status',       color: 'yellow', icon: '⏸',   desc: 'Pauses or enables campaigns based on performance + inventory' },
  budget:        { label: 'Budget',       color: 'purple', icon: '💰',  desc: 'Adjusts daily budgets with day-of-week multipliers' },
  dayparting:    { label: 'Dayparting',   color: 'orange', icon: '🕐',  desc: 'Turns campaigns on/off at specific hours per timezone' },
  revive:        { label: 'Revive',       color: 'teal',   icon: '↑',   desc: 'Resurrects dead keywords with gradual bid increases' },
  placement:     { label: 'Placement',    color: 'indigo', icon: '📍',  desc: 'Adjusts Top of Search % and Product Pages % modifiers' },
  default_bid:   { label: 'Default Bid',  color: 'gray',   icon: '⚙',   desc: 'Optimizes auto campaign ad group default bids' },
  negative_word: { label: 'Neg. Word',    color: 'pink',   icon: '✂',   desc: 'Per-word aggregation and negation of irrelevant terms' },
};

// ── Component ────────────────────────────────────────────────────────────────

export function PpcOverview() {
  const [activeAccounts, setActiveAccounts] = useState<Account[]>([]);
  const [ppcData, setPpcData] = useState<OverviewData | null>(null);
  const [autoStatus, setAutoStatus] = useState<AutoStatus | null>(null);
  const [recentLog, setRecentLog] = useState<LogEntry[]>([]);
  const [outcomes, setOutcomes] = useState<OutcomeData | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedMarketplace, setSelectedMarketplace] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [singleDate, setSingleDate] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [allMarketplaces, setAllMarketplaces] = useState<string[]>([]);

  // Load accounts once
  useEffect(() => {
    api.get<Account[]>('/accounts').then(accts => {
      const active = accts.filter(a => a.is_active !== 0);
      setActiveAccounts(active);
      if (active.length === 0) setInitialLoading(false);
    }).catch(() => setInitialLoading(false));
  }, []);

  // Load data when accounts/marketplace/days change
  useEffect(() => {
    if (activeAccounts.length === 0) return;
    (async () => {
      setRefreshing(true);
      setError('');
      try {
        const mpParam = selectedMarketplace ? `&marketplace=${selectedMarketplace}` : '';
        let dateParam: string;
        if (singleDate) {
          dateParam = `start=${singleDate}&end=${singleDate}`;
        } else if (customFrom && customTo) {
          dateParam = `start=${customFrom}&end=${customTo}`;
        } else {
          dateParam = `days=${days}`;
        }
        const logDays = singleDate ? 1 : (customFrom && customTo ? Math.ceil((new Date(customTo).getTime() - new Date(customFrom).getTime()) / 86400000) + 1 : days);
        const [ppcResults, statusResults, logResults, out] = await Promise.all([
          Promise.all(activeAccounts.map(a => api.get<OverviewData>(`/ppc/overview/${a.id}?${dateParam}${mpParam}`).catch(() => null))),
          Promise.all(activeAccounts.map(a => api.get<AutoStatus>(`/automation/status?accountId=${a.id}`).catch(() => null))),
          Promise.all(activeAccounts.map(a => api.get<{ entries: LogEntry[] }>(`/automation/log?accountId=${a.id}&daysBack=${logDays}&limit=8`).catch(() => ({ entries: [] })))),
          api.get<OutcomeData>('/ppc/outcomes').catch(() => null),
        ]);

        // Merge PPC data
        const merged: OverviewData = { thisWeek: { spend: 0, sales: 0, orders: 0, impressions: 0, clicks: 0, acos: null }, lastWeek: { spend: 0, sales: 0, orders: 0, impressions: 0, clicks: 0, acos: null }, activeCampaigns: 0, pendingActions: 0, marketplaces: [], topCampaigns: [] };
        const mpSet = new Set<string>();
        for (const ppc of ppcResults) {
          if (!ppc) continue;
          merged.thisWeek.spend += ppc.thisWeek.spend; merged.thisWeek.sales += ppc.thisWeek.sales;
          merged.thisWeek.orders += ppc.thisWeek.orders; merged.thisWeek.impressions += ppc.thisWeek.impressions;
          merged.thisWeek.clicks += ppc.thisWeek.clicks;
          merged.lastWeek.spend += ppc.lastWeek.spend; merged.lastWeek.sales += ppc.lastWeek.sales;
          merged.lastWeek.orders += ppc.lastWeek.orders; merged.lastWeek.impressions += ppc.lastWeek.impressions;
          merged.lastWeek.clicks += ppc.lastWeek.clicks;
          merged.activeCampaigns += ppc.activeCampaigns; merged.pendingActions += ppc.pendingActions;
          merged.topCampaigns.push(...ppc.topCampaigns);
          for (const mp of (ppc.marketplaces ?? [])) mpSet.add(mp);
        }
        merged.thisWeek.acos = merged.thisWeek.sales > 0 ? merged.thisWeek.spend / merged.thisWeek.sales : null;
        merged.lastWeek.acos = merged.lastWeek.sales > 0 ? merged.lastWeek.spend / merged.lastWeek.sales : null;
        merged.topCampaigns.sort((a, b) => b.spend - a.spend);
        merged.topCampaigns = merged.topCampaigns.slice(0, 10);
        if (!selectedMarketplace) setAllMarketplaces([...mpSet].sort());

        // Merge automation status
        const mergedStatus: AutoStatus = { rules: [], recentActivity: [], criteriaProfiles: 0, objectives: 0, activeAssignments: 0 };
        for (const s of statusResults) {
          if (!s) continue;
          mergedStatus.rules.push(...s.rules);
          mergedStatus.recentActivity.push(...s.recentActivity);
          mergedStatus.criteriaProfiles = Math.max(mergedStatus.criteriaProfiles, s.criteriaProfiles);
          mergedStatus.objectives += s.objectives;
          mergedStatus.activeAssignments += s.activeAssignments;
        }

        const allLogs = logResults.flatMap(r => r.entries);
        allLogs.sort((a, b) => b.created_at.localeCompare(a.created_at));

        setPpcData(merged);
        setAutoStatus(mergedStatus);
        setRecentLog(allLogs.slice(0, 8));
        setOutcomes(out);
      } catch (e) { setError(String(e)); }
      setRefreshing(false);
      setInitialLoading(false);
    })();
  }, [activeAccounts, selectedMarketplace, days, singleDate, customFrom, customTo]);

  // Aggregate rule counts by algorithm type
  const ruleCounts = new Map<string, { active: number; total: number }>();
  if (autoStatus) {
    for (const r of autoStatus.rules) {
      const key = r.algorithm_type;
      const existing = ruleCounts.get(key) ?? { active: 0, total: 0 };
      existing.total += r.cnt;
      if (r.is_active) existing.active += r.cnt;
      ruleCounts.set(key, existing);
    }
  }

  // Aggregate recent activity by algorithm type
  const activityCounts = new Map<string, number>();
  if (autoStatus) {
    for (const a of autoStatus.recentActivity) {
      if (a.mode === 'live') {
        activityCounts.set(a.algorithm_type, (activityCounts.get(a.algorithm_type) ?? 0) + a.cnt);
      }
    }
  }

  const totalActiveRules = Array.from(ruleCounts.values()).reduce((s, v) => s + v.active, 0);
  const totalExecutions7d = Array.from(activityCounts.values()).reduce((s, v) => s + v, 0);
  const periodLabel = singleDate ? ` (${singleDate})` : customFrom && customTo ? ` (${customFrom} to ${customTo})` : ` (${days}d)`;

  if (initialLoading) return <LoadingSkeleton />;

  return (
    <div className={`space-y-6 ${refreshing ? 'opacity-60 pointer-events-none' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            PPC Autopilot {refreshing && <span className="ml-2 text-sm font-normal text-gray-400">Loading...</span>}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            AI-powered PPC management — rules created by Claude, validated by Workers AI, monitored here
          </p>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {/* Marketplace filter + Date range */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2">
          <button onClick={() => setSelectedMarketplace(null)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
              !selectedMarketplace ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}>All Markets</button>
          {allMarketplaces.map(mp => (
            <button key={mp} onClick={() => setSelectedMarketplace(mp === selectedMarketplace ? null : mp)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                selectedMarketplace === mp ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}>{MP_NAMES[mp] ?? mp}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {DATE_RANGES.map(r => (
            <button key={r.days} onClick={() => { setDays(r.days); setSingleDate(''); setCustomFrom(''); setCustomTo(''); }}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                days === r.days && !singleDate && !customFrom ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}>{r.label}</button>
          ))}
          <span className="mx-1 text-gray-300">|</span>
          <input type="date" value={singleDate || customFrom} onChange={e => {
            const val = e.target.value;
            if (customTo || customFrom) {
              // Already in range mode — update from
              setCustomFrom(val); setSingleDate('');
            } else {
              // First pick — single date mode
              setSingleDate(val); setCustomFrom(''); setCustomTo('');
            }
          }} className={`rounded-lg border px-2 py-1 text-sm ${singleDate || customFrom ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600'}`} />
          {(singleDate || customFrom) && (
            <>
              <span className="text-gray-400 text-xs">to</span>
              <input type="date" value={customTo} onChange={e => {
                setCustomTo(e.target.value); setSingleDate('');
                if (!customFrom && singleDate) setCustomFrom(singleDate);
              }} className={`rounded-lg border px-2 py-1 text-sm ${customTo ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600'}`} />
              <button onClick={() => { setSingleDate(''); setCustomFrom(''); setCustomTo(''); setDays(7); }}
                className="text-gray-400 hover:text-gray-600 text-sm ml-1">&times;</button>
            </>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      {ppcData && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <KpiCard label={`Spend${periodLabel}`} value={`$${ppcData.thisWeek.spend.toFixed(0)}`}
            delta={ppcData.lastWeek.spend > 0 ? ((ppcData.thisWeek.spend - ppcData.lastWeek.spend) / ppcData.lastWeek.spend) * 100 : null}
            deltaLabel={`vs prior period`} />
          <KpiCard label={`Sales${periodLabel}`} value={`$${ppcData.thisWeek.sales.toFixed(0)}`}
            delta={ppcData.lastWeek.sales > 0 ? ((ppcData.thisWeek.sales - ppcData.lastWeek.sales) / ppcData.lastWeek.sales) * 100 : null}
            deltaLabel={`vs prior period`} />
          <KpiCard label={`ACoS${periodLabel}`}
            value={ppcData.thisWeek.acos !== null ? `${(ppcData.thisWeek.acos * 100).toFixed(1)}%` : '--'}
            delta={ppcData.thisWeek.acos !== null && ppcData.lastWeek.acos !== null && ppcData.lastWeek.acos > 0
              ? ((ppcData.thisWeek.acos - ppcData.lastWeek.acos) / ppcData.lastWeek.acos) * 100 : null}
            invertDelta deltaLabel={`vs prior period`} />
          <KpiCard label="Active Rules" value={String(totalActiveRules)} sub={`${autoStatus?.criteriaProfiles ?? 0} profiles`} />
          <KpiCard label={`Actions${periodLabel}`} value={String(totalExecutions7d)}
            sub={ppcData.pendingActions > 0 ? `${ppcData.pendingActions} pending` : 'all executed'}
            highlight={ppcData.pendingActions > 0} />
        </div>
      )}

      {/* Algorithm Status Grid — Scale Insights inspired */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700">Algorithms</h2>
          <Link to="/ppc/automation" className="text-xs font-medium text-brand-600 hover:text-brand-700">
            Manage Rules &rarr;
          </Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {Object.entries(ALGO_META).map(([key, meta]) => {
            const counts = ruleCounts.get(key);
            const executions = activityCounts.get(key) ?? 0;
            const hasRules = counts && counts.active > 0;
            return (
              <Link key={key} to={`/ppc/automation?filter=${key}`}
                className={`rounded-xl border p-4 transition-all hover:shadow-md ${
                  hasRules ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50'
                }`}>
                <div className="flex items-start justify-between">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold bg-${meta.color}-100 text-${meta.color}-700`}>
                    {meta.icon}
                  </div>
                  {hasRules && (
                    <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                      Active
                    </span>
                  )}
                </div>
                <h3 className="mt-2 text-sm font-semibold text-gray-800">{meta.label}</h3>
                <p className="mt-0.5 text-[11px] leading-tight text-gray-400">{meta.desc}</p>
                <div className="mt-2 flex items-center gap-3 text-xs">
                  <span className="text-gray-500">
                    {counts ? `${counts.active}/${counts.total} rules` : 'No rules'}
                  </span>
                  {executions > 0 && (
                    <span className="text-green-600">{executions} exec</span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Two-column layout: Objectives + AI Outcomes */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Objectives Summary */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Strategic Objectives</h3>
            <Link to="/ppc/automation?tab=objectives" className="text-xs text-brand-600 hover:text-brand-700">View all</Link>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-gray-100 p-3 text-center">
              <p className="text-xl font-bold text-gray-900">{autoStatus?.objectives ?? 0}</p>
              <p className="text-[11px] text-gray-400">Objectives</p>
            </div>
            <div className="rounded-lg border border-gray-100 p-3 text-center">
              <p className="text-xl font-bold text-gray-900">{autoStatus?.activeAssignments ?? 0}</p>
              <p className="text-[11px] text-gray-400">Assignments</p>
            </div>
            <div className="rounded-lg border border-gray-100 p-3 text-center">
              <p className="text-xl font-bold text-gray-900">{autoStatus?.criteriaProfiles ?? 0}</p>
              <p className="text-[11px] text-gray-400">Profiles</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-400">
            Objectives bundle multiple rules into reusable strategies assigned to campaigns.
            Create them via Claude or the Rules page.
          </p>
        </div>

        {/* AI Outcomes */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-gray-700">AI Learning — Outcomes</h3>
          {outcomes && outcomes.summary.total > 0 ? (
            <div className="mt-3 grid grid-cols-4 gap-2">
              <div className="rounded-lg border border-gray-100 p-2 text-center">
                <p className="text-lg font-bold text-gray-900">{outcomes.summary.total}</p>
                <p className="text-[10px] text-gray-400">Measured</p>
              </div>
              <div className="rounded-lg border border-green-100 bg-green-50/50 p-2 text-center">
                <p className="text-lg font-bold text-green-600">{outcomes.summary.improved}</p>
                <p className="text-[10px] text-gray-400">Improved</p>
              </div>
              <div className="rounded-lg border border-red-100 bg-red-50/50 p-2 text-center">
                <p className="text-lg font-bold text-red-600">{outcomes.summary.worsened}</p>
                <p className="text-[10px] text-gray-400">Worsened</p>
              </div>
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-2 text-center">
                <p className="text-lg font-bold text-blue-600">{outcomes.summary.successRate}%</p>
                <p className="text-[10px] text-gray-400">Success</p>
              </div>
            </div>
          ) : (
            <p className="mt-3 text-xs text-gray-400">
              No outcome data yet. Actions need 7 days after execution to measure impact.
            </p>
          )}
          <p className="mt-3 text-xs text-gray-400">
            Workers AI validates every bid/budget change before execution. Outcomes measured 7 days after.
          </p>
        </div>
      </div>

      {/* Recent Automation Activity */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Recent Activity</h3>
          <Link to="/ppc/log" className="text-xs text-brand-600 hover:text-brand-700">Full Log &rarr;</Link>
        </div>
        {recentLog.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-400">
            No automation activity yet. Create rules via Claude or the Rules page to get started.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Time</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Algorithm</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Rule</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Target</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Change</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Mode</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentLog.map((e, i) => {
                  const meta = ALGO_META[e.algorithm_type];
                  return (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="py-1.5 text-xs text-gray-400 whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="py-1.5">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-${meta?.color ?? 'gray'}-100 text-${meta?.color ?? 'gray'}-700`}>
                          {meta?.label ?? e.algorithm_type}
                        </span>
                      </td>
                      <td className="py-1.5 text-gray-700 text-xs">{e.rule_name}</td>
                      <td className="py-1.5 text-xs">
                        <div className="text-gray-700 truncate max-w-[160px]">{e.campaign_name}</div>
                        {e.keyword_text && <div className="text-gray-400 truncate max-w-[160px]">{e.keyword_text}</div>}
                      </td>
                      <td className="py-1.5 text-xs">
                        <span className="text-gray-400">{e.old_value || '—'}</span>
                        <span className="mx-1 text-gray-300">&rarr;</span>
                        <span className="font-medium text-gray-800">{e.new_value}</span>
                      </td>
                      <td className="py-1.5">
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                          e.mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>{e.mode}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top Campaigns */}
      {ppcData && ppcData.topCampaigns.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Campaigns by Spend</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Campaign</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Spend</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Sales</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">ACoS</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">ROAS</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Budget</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {ppcData.topCampaigns.map(c => (
                  <tr key={c.id}>
                    <td className="py-1.5 max-w-[200px] truncate font-medium text-gray-800" title={c.name}>{c.name}</td>
                    <td className="py-1.5 text-right text-gray-600">${c.spend.toFixed(2)}</td>
                    <td className="py-1.5 text-right text-gray-600">${c.sales.toFixed(2)}</td>
                    <td className={`py-1.5 text-right font-medium ${
                      c.acos !== null ? (c.acos > 0.3 ? 'text-red-600' : c.acos > 0.2 ? 'text-yellow-600' : 'text-green-600') : 'text-gray-400'
                    }`}>
                      {c.acos !== null ? `${(c.acos * 100).toFixed(1)}%` : '--'}
                    </td>
                    <td className="py-1.5 text-right text-gray-600">{c.roas !== null ? `${c.roas.toFixed(1)}x` : '--'}</td>
                    <td className="py-1.5 text-right">
                      {c.budgetUtilization !== null ? (
                        <div className="flex items-center justify-end gap-1">
                          <div className="h-1.5 w-12 rounded-full bg-gray-200">
                            <div className={`h-1.5 rounded-full ${c.budgetUtilization > 90 ? 'bg-red-400' : c.budgetUtilization > 70 ? 'bg-yellow-400' : 'bg-green-400'}`}
                              style={{ width: `${Math.min(c.budgetUtilization, 100)}%` }} />
                          </div>
                          <span className="text-[10px] text-gray-400">{c.budgetUtilization.toFixed(0)}%</span>
                        </div>
                      ) : '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Link to="/ppc/automation" className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
          Manage Rules
        </Link>
        <Link to="/ppc/log" className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
          Change Log
        </Link>
        {ppcData && ppcData.pendingActions > 0 && (
          <Link to="/ppc/actions" className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-2 text-sm font-medium text-yellow-700 hover:bg-yellow-100">
            Review {ppcData.pendingActions} Pending Actions
          </Link>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, delta, invertDelta, sub, highlight, deltaLabel }: {
  label: string; value: string; delta?: number | null; invertDelta?: boolean; sub?: string; highlight?: boolean; deltaLabel?: string;
}) {
  const isGood = delta != null ? (invertDelta ? delta < 0 : delta > 0) : false;
  const isBad = delta != null ? (invertDelta ? delta > 0 : delta < 0) : false;
  return (
    <div className={`rounded-xl border bg-white p-4 ${highlight ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'}`}>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-bold text-gray-900">{value}</p>
      {delta != null && (
        <p className={`mt-0.5 text-xs font-medium ${isGood ? 'text-green-600' : isBad ? 'text-red-600' : 'text-gray-400'}`}>
          {delta > 0 ? '+' : ''}{delta.toFixed(1)}% {deltaLabel ?? 'vs prior period'}
        </p>
      )}
      {sub && <p className="mt-0.5 text-[11px] text-gray-400">{sub}</p>}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-48 rounded bg-gray-200" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-20 rounded-xl bg-gray-200" />)}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => <div key={i} className="h-32 rounded-xl bg-gray-200" />)}
      </div>
    </div>
  );
}
